import { useEffect, useReducer, useState } from 'react';
import {
  Action,
  initSections,
  reducer,
} from '../../../survey_engine/SurveyTemplates/dispatcher';
import { SectionData } from '../../../survey_engine/SurveyTemplates/Section';
import React from 'react';
import { PageHeader } from '../../../survey_engine/SurveyTemplates/PageHeader';
import { Card } from '../../../../components/Card';
import { CardHeader } from '../../../survey_engine/SurveyTemplates/CardHeader';
import { SectionList } from '../../../survey_engine/SurveyTemplates/SectionList';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import { FIELD_CONFIGS, fetchTemplate, areSectionsSavable } from '../data';
import { NameInput } from '../../../survey_engine/SurveyTemplates/NameInput';
import { getRedirectUriQueryParam } from '../../../../utils/url';
import { InterviewKitTemplateService } from '../../../../services/v1/applicant_tracking/InterviewKitTemplateService';
import { AlertObject, AlertType } from '../../../../components/Alert';
import { DefaultAlert } from '../../../../components/DefaultAlert';

const REDIRECT_URI =
  getRedirectUriQueryParam() ?? '/organization_settings/interview_questions';

interface PropTypes {
  id: number;
}

export default function EditInterviewKitTemplatePage(props: PropTypes) {
  const [name, setName] = useState<string>('');
  const [sections, dispatch] = useReducer(reducer, null, initSections);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [alert, setAlert] = useState<AlertObject>(null);
  const [error, setError] = useState<string>(null);

  useEffect(() => {
    async function load() {
      const template = await fetchTemplate(props.id.toString());
      setName(template.name);
      dispatch({ type: 'reset', newState: template.sections });
      setIsLoading(false);
    }

    load();
  }, [props.id]);

  function isSavable() {
    if (!name.trim()) return false;
    if (!areSectionsSavable(sections)) return false;

    return true;
  }

  async function handleSave() {
    setIsLoading(true);

    try {
      const formattedSections = sections.map(({ sId, name, fields }) => ({
        id: sId,
        name,
        questions: fields.map(
          ({ serverId, title, fieldType, max, aiFillEnabled }) => ({
            id: serverId,
            title,
            fieldType,
            max,
            aiFillEnabled,
          }),
        ),
      }));

      await InterviewKitTemplateService.update(props.id, {
        name,
        sections: formattedSections,
      });
      window.location.href = REDIRECT_URI;
    } catch ({ status, body }) {
      if (status === 409) {
        const message = body?.message;
        setAlert({ type: AlertType.Danger, message });
        setError(message);
      }
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (error) setError(null);
  }, [name]);

  return (
    <>
      {isLoading && <LoadingSpinner showBackdrop />}
      <DefaultAlert alert={alert} setAlert={setAlert} />
      <Body
        isSavable={isSavable()}
        name={name}
        error={error}
        sections={sections}
        onName={setName}
        dispatch={dispatch}
        onSave={handleSave}
      />
    </>
  );
}

function Body(props: {
  isSavable: boolean;
  name: string;
  error: string;
  onName: (value: string) => void;
  sections: SectionData[];
  dispatch: React.Dispatch<Action>;
  onSave: () => void;
}) {
  return (
    <div className='container'>
      <PageHeader title={props.name} backUrl={REDIRECT_URI} />
      <Card>
        <CardHeader
          title='Edit Interview Questions'
          isSavable={props.isSavable}
          onSaveClick={props.onSave}
          cancelUrl={REDIRECT_URI}
        />
        <div className='border-bottom'>
          <div className='d-flex flex-column w-50 fs-5 pt-2 pb-4 gap-4'>
            <NameInput
              name={props.name}
              placeholder='Insert Name'
              error={props.error}
              onName={props.onName}
            />
          </div>
        </div>
        <SectionList
          fieldConfigs={FIELD_CONFIGS}
          singleTypeSections={true}
          sections={props.sections}
          dispatch={props.dispatch}
          title='Questions'
          subtitle='Define and manage your interview questions.'
          addFieldButtonText='Add Question'
          fieldDeleteTooltip='Remove Question'
          defaultFieldType='text'
          columns={[
            {
              fieldName: 'text',
              className: 'col',
              label: 'Questions',
              helperText: 'Maximum 255 characters',
              placeholder: 'Insert Question',
            },
          ]}
        />
      </Card>
    </div>
  );
}
